import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Icon,
  IconButton,
  Stack,
  P,
  Popover,
  Card,
  Clickable,
  IconName,
} from '@fashionnovateam/nova-ui-toolkit';
import cn from 'classnames';
import {
  PurchaseOrder,
  BulkProdSubStage,
} from '../../Lib/Types/purchase-order';
import getPoStatus from '../../Lib/Utils/Helpers/make-button-text';
import PoStatus from '../../Lib/Constants/purchase-order-status';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';
import {
  useAcceptPurchaseOrder,
  useUpdatePoBulkProductionSubStage,
} from '../../Lib/Hooks/purchase-orders.hooks';

export type SelectModalProps = {
  selectedPos: string[];
  purchaseOrders: PurchaseOrder[];
  cleanSelectedPos: () => void;
};

type ModalOption = {
  id: string;
  label: string;
  icon?: IconName;
  action?: () => void | undefined;
  isEnabled: () => boolean;
  subOptions?: ModalOption[];
  hasSubMenu?: boolean;
};

type SelectModalOptionsItemProps = {
  option: ModalOption;
};

function SelectModalOptionsItem({ option }: SelectModalOptionsItemProps) {
  return (
    <Clickable
      data-testid="select-modal-options-item"
      onClick={() => option.action?.()}
      className={cn(
        'self-stretch px-3 py-2',
        option.isEnabled() && 'hover:bg-neutral-100',
      )}
      disabled={!option.isEnabled()}
    >
      <Stack direction="row" className="gap-[10px] justify-between w-full">
        <Stack direction="row" className="gap-[10px]">
          {option.icon && (
            <Icon name={option.icon} className="text-[#1D24338C] text-md" />
          )}
          <P color={option.isEnabled() ? 'primary' : 'disabled'}>
            {option.label}
          </P>
        </Stack>
        {option.hasSubMenu && (
          <Icon name="chevron/chevron-right" className="transition-transform" />
        )}
      </Stack>
    </Clickable>
  );
}

function SelectModalOptionsItemWithSubOptions({
  option,
}: SelectModalOptionsItemProps) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 620);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const popoverConfig = useMemo(
    () => ({
      position: isMobile ? ('top' as const) : ('right-top' as const),
      offset: isMobile ? -45 : 10,
    }),
    [isMobile],
  );
  return (
    <Stack
      data-testid="select-modal-options-item-with-sub-options"
      align="center"
      direction="row"
      className="w-full"
    >
      <Popover position={popoverConfig.position} offset={popoverConfig.offset}>
        <Popover.Clickable
          onClick={() => option.action?.()}
          className={cn('self-stretch px-3 py-2 w-full', {
            'hover:bg-neutral-100': option.isEnabled(),
          })}
          disabled={!option.isEnabled()}
        >
          <Stack direction="row" className="gap-[10px] justify-between w-full">
            <Stack direction="row" className="gap-[10px]">
              {option.icon && (
                <Icon name={option.icon} className="text-[#1D24338C] text-md" />
              )}
              <P color={option.isEnabled() ? 'primary' : 'disabled'}>
                {option.label}
              </P>
            </Stack>
            <Icon
              name="chevron/chevron-right"
              className="transition-transform"
            />
          </Stack>
        </Popover.Clickable>
        <Popover.Content>
          <Card className="w-[301px] text-[#1D2433] text-xsm shadow-md">
            <Card.Body>
              {option.subOptions?.map((subOption) => (
                <Clickable
                  key={subOption.id}
                  onClick={subOption.action}
                  className={cn(
                    'self-stretch px-3 py-2',
                    subOption.isEnabled() && 'hover:bg-neutral-100',
                  )}
                  disabled={!subOption.isEnabled()}
                >
                  <Stack direction="row" className="gap-[10px]">
                    {subOption.icon && (
                      <Icon
                        name={subOption.icon}
                        className="text-[#1D24338C] text-md"
                      />
                    )}
                    <P color={subOption.isEnabled() ? 'primary' : 'disabled'}>
                      {subOption.label}
                    </P>
                  </Stack>
                </Clickable>
              ))}
            </Card.Body>
          </Card>
        </Popover.Content>
      </Popover>
    </Stack>
  );
}

export type SelectModalOptionsProps = {
  selectedPos: string[];
  purchaseOrders: PurchaseOrder[];
  options: ModalOption[];
};

export function SelectModalOptions({ options }: SelectModalOptionsProps) {
  return (
    <Card
      data-testid="select-modal-options"
      className="mb-8 mr-40 w-[301px] text-[#1D2433] text-xsm"
    >
      <Card.Body>
        {options.map((option) => (
          <React.Fragment key={option.id}>
            {option.hasSubMenu && option.subOptions ? (
              <SelectModalOptionsItemWithSubOptions option={option} />
            ) : (
              <SelectModalOptionsItem option={option} />
            )}
          </React.Fragment>
        ))}
      </Card.Body>
    </Card>
  );
}

export function SelectModal({
  selectedPos,
  purchaseOrders,
  cleanSelectedPos,
}: SelectModalProps) {
  const location = useLocation();
  const { mutate: acceptPurchaseOrderMutation } = useAcceptPurchaseOrder();
  const { mutate: updatePoBulkProductionSubStage } =
    useUpdatePoBulkProductionSubStage();
  const { flags } = React.useContext(FeatureFlagsContext);
  const userId = useMemo(() => location.pathname.split('/')[2], [location]);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleAcceptAllPurchaseOrders = () => {
    selectedPos.forEach((poId) =>
      acceptPurchaseOrderMutation({
        userId,
        purchaseOrderId: poId,
      }),
    );
    cleanSelectedPos();
    toast(`Successfully accepted ${selectedPos.length} POs.`, {
      type: 'success',
    });
  };

  function handleBulkProdUpdate(subStage: BulkProdSubStage) {
    setIsUpdating(true);

    const promises = selectedPos.map((poId) =>
      updatePoBulkProductionSubStage({
        userId,
        purchaseOrderId: poId,
        sub_stage: subStage,
      }),
    );

    Promise.all(promises)
      .then(() => {
        toast(
          `Successfully updated the status on ${selectedPos.length} POs to ${subStage}.`,
          { type: 'success' },
        );
      })
      .catch((error) => {
        toast(`Error updating POs: ${error.message}`, { type: 'error' });
      })
      .finally(() => {
        setIsUpdating(false);
        cleanSelectedPos();
      });
  }

  const modalOptions: ModalOption[] = useMemo(
    () => [
      {
        id: 'accept-all',
        label: 'Accept All',
        icon: 'check/check-tick-square-broken',
        action: handleAcceptAllPurchaseOrders,
        isEnabled: () =>
          selectedPos
            .map((poId) => {
              const purchaseOrder = purchaseOrders.find(
                (po) => po.po_number === poId,
              );
              if (!purchaseOrder) return false;

              const acceptButtonText = getPoStatus({
                revised_at: purchaseOrder.revised_at,
                vendor_rejection_reason: purchaseOrder.vendor_rejection_reason,
                revision_accepted_at: purchaseOrder.revision_accepted_at,
              });

              return acceptButtonText === PoStatus.ACCEPT;
            })
            .every((valid) => valid),
      },
      {
        id: 'bulk-prod-update',
        label: 'Update Status',
        icon: 'circle-dotted',
        hasSubMenu: true,
        action: undefined,
        isEnabled: () =>
          selectedPos
            .map((poId) => {
              const purchaseOrder = purchaseOrders.find(
                (po) => po.po_number === poId,
              );
              if (!purchaseOrder) return false;
              return purchaseOrder.stage === 'Bulk Production';
            })
            .every((valid) => valid),
        subOptions: [
          {
            id: 'ready-for-prod',
            label: 'Ready for Prod',
            action: () => handleBulkProdUpdate('Ready for Production'),
            isEnabled: () => true,
          },
          {
            id: 'buy-materials',
            label: 'Buy Materials',
            action: () => handleBulkProdUpdate('Buy Materials'),
            isEnabled: () => true,
          },
          {
            id: 'cutting-sewing',
            label: 'Cutting/Sewing',
            action: () => handleBulkProdUpdate('Cutting/Sewing'),
            isEnabled: () => true,
          },
          {
            id: 'washing',
            label: 'Washing',
            action: () => handleBulkProdUpdate('Washing'),
            isEnabled: () => true,
          },
          {
            id: 'packaging',
            label: 'Packaging',
            action: () => handleBulkProdUpdate('Packaging'),
            isEnabled: () => true,
          },
          {
            id: 'production-completed',
            label: 'Production Completed',
            action: () => handleBulkProdUpdate('Production Completed'),
            isEnabled: () => true,
          },
        ],
      },
    ],
    [
      selectedPos,
      purchaseOrders,
      flags,
      handleAcceptAllPurchaseOrders,
      handleBulkProdUpdate,
    ],
  );

  return (
    <Stack
      data-testid="select-modal"
      direction="row"
      width="none"
      align="center"
      className={cn(
        'fixed bottom-20 left-1/2 -translate-x-1/2 w-[307px] p-5 gap-5 rounded-2xl bg-neutral-0',
        'shadow-[0_2px_40px_0_rgba(0,0,0,0.2)]',
      )}
    >
      <Stack direction="row" align="center" gap="md">
        <IconButton
          size="sm"
          color="secondary"
          label="unselect"
          onClick={cleanSelectedPos}
          icon={<Icon name="multiple-cross/multiple-cross-cancel-default" />}
        />
        <P size="sm" weight="semibold">
          {selectedPos.length} Selected
        </P>
      </Stack>
      <Popover position="top">
        <Popover.Button disabled={isUpdating}>
          {isUpdating ? (
            <>
              <Icon name="loading-circle" className="animate-spin mr-1" />
              Updating...
            </>
          ) : (
            <>
              Actions <Icon name="chevron/chevron-down" />
            </>
          )}
        </Popover.Button>
        <Popover.Content>
          <SelectModalOptions
            selectedPos={selectedPos}
            purchaseOrders={purchaseOrders}
            options={modalOptions}
          />
        </Popover.Content>
      </Popover>
    </Stack>
  );
}
